import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, Zap, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/perms";

const STATUSES = [["nuova", "Nuova"], ["in_valutazione", "In valutazione"], ["attivata", "Attivata"], ["rifiutata", "Rifiutata"]];
const STATUS_CLS = { nuova: "bg-red-100 text-red-700", in_valutazione: "bg-amber-100 text-amber-800", attivata: "bg-emerald-100 text-emerald-800", rifiutata: "bg-slate-200 text-slate-700" };
const dt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "");
const uniq = (rows, k, l) => [...new Map(rows.map((r) => [r[k], r[l]])).entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));

function FilterSelect({ value, onChange, all, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function CourseStats({ rows }) {
  const stats = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const s = (m[r.course_id] ||= { title: r.course_title, total: 0, open: 0, clients: new Set() });
      s.total += 1; s.clients.add(r.client_id);
      if (r.status === "nuova" || r.status === "in_valutazione") s.open += 1;
    });
    return Object.entries(m).sort((a, b) => b[1].total - a[1].total);
  }, [rows]);
  if (!stats.length) return null;
  return (
    <Card className="border-slate-200"><CardContent className="p-5">
      <div className="font-display font-bold text-lg mb-3">Richieste per corso</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {stats.map(([id, s]) => (
          <div key={id} className="rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`activation-stat-${id}`}>
            <div className="font-display text-2xl font-extrabold text-[#0F2942] w-10 text-center" data-testid={`activation-stat-total-${id}`}>{s.total}</div>
            <div className="min-w-0"><div className="font-medium text-sm truncate">{s.title}</div>
              <div className="text-xs text-slate-500">{s.clients.size} clienti · {s.open} da gestire</div></div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function ActivateDialog({ req, onClose, onDone }) {
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrice(""); }, [req]);
  const submit = async (e) => {
    e.preventDefault();
    const v = Number(String(price).replace(",", "."));
    if (price === "" || Number.isNaN(v) || v < 0) return toast.error("Inserisci un prezzo valido");
    setBusy(true);
    try {
      await api.post(`/activation-requests/${req.id}/activate`, { price: v });
      toast.success("Corso attivato e aggiunto al catalogo del cliente");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="activate-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Attiva e aggiungi al catalogo</DialogTitle>
            <DialogDescription>{req ? `${req.company_name || req.client_name} · ${req.course_title}` : ""}</DialogDescription>
          </DialogHeader>
          <div><Label htmlFor="act-price">Prezzo personalizzato (imponibile, IVA esclusa) *</Label>
            <Input id="act-price" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} autoFocus data-testid="activate-price" /></div>
          <p className="text-xs text-slate-500">Il corso verrà aggiunto al catalogo del cliente, la richiesta passerà ad "Attivata" e il cliente riceverà un'email.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="activate-cancel">Annulla</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="activate-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Attiva corso
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ActivationRequests() {
  const { user } = useAuth();
  const canActivate = can(user, "clients");
  const [act, setAct] = useState(null);
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ client: "all", course: "all", status: "all", from: "", to: "" });
  const [del, setDel] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const load = () => api.get("/activation-requests").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.post("/activation-requests/mark-all-seen").then(() => window.dispatchEvent(new Event("badges:refresh")));
  }, []);

  const shown = useMemo(() => (rows || []).filter((r) => {
    const d = (r.created_at || "").slice(0, 10);
    return (f.client === "all" || r.client_id === f.client) && (f.course === "all" || r.course_id === f.course)
      && (f.status === "all" || r.status === f.status) && (!f.from || d >= f.from) && (!f.to || d <= f.to);
  }), [rows, f]);

  const changeStatus = async (r, status) => {
    try { await api.put(`/activation-requests/${r.id}/status`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/activation-requests/${del.id}`); toast.success("Richiesta eliminata"); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-6" data-testid="activation-requests-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Richieste Attivazione Corsi</h1>
        <p className="text-slate-500 mt-1">Richieste dei clienti per corsi non ancora presenti nel loro catalogo. Per attivare un corso aggiungilo al catalogo del cliente con il relativo prezzo.</p>
      </div>
      <CourseStats rows={rows} />
      <Card className="border-slate-200"><CardContent className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div><Label className="text-xs">Cliente</Label><FilterSelect value={f.client} onChange={set("client")} all="Tutti i clienti" options={uniq(rows, "client_id", "company_name")} testid="activation-filter-client" /></div>
        <div><Label className="text-xs">Corso</Label><FilterSelect value={f.course} onChange={set("course")} all="Tutti i corsi" options={uniq(rows, "course_id", "course_title")} testid="activation-filter-course" /></div>
        <div><Label className="text-xs">Stato</Label><FilterSelect value={f.status} onChange={set("status")} all="Tutti gli stati" options={STATUSES} testid="activation-filter-status" /></div>
        <div><Label className="text-xs">Dal</Label><Input type="date" value={f.from} onChange={set("from")} data-testid="activation-filter-from" /></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={f.to} onChange={set("to")} data-testid="activation-filter-to" /></div>
      </CardContent></Card>
      <Card className="border-slate-200"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]" data-testid="activation-requests-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Corso richiesto</th><th className="p-3 w-48">Stato</th><th className="p-3 w-56" /></tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`activation-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap">{dt(r.created_at)}</td>
                <td className="p-3"><div className="font-medium">{r.company_name || "—"}</div><div className="text-xs text-slate-500">{r.client_name}</div></td>
                <td className="p-3">{r.course_title}</td>
                <td className="p-3">
                  <Select value={r.status} onValueChange={(v) => changeStatus(r, v)}>
                    <SelectTrigger className={`h-8 border-0 font-semibold ${STATUS_CLS[r.status]}`} data-testid={`activation-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3"><div className="flex items-center justify-end gap-1">
                  {canActivate && r.billing_mode === "mensile" && (r.status === "nuova" || r.status === "in_valutazione") && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" onClick={() => setAct(r)} data-testid={`activation-activate-${r.id}`}>
                      <Zap className="w-4 h-4 mr-1" /> Attiva e aggiungi al catalogo
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDel(r)} data-testid={`activation-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div></td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500" data-testid="activation-empty">Nessuna richiesta trovata.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <ActivateDialog req={act} onClose={() => setAct(null)} onDone={() => { setAct(null); load(); }} />
      <AlertDialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }}>
        <AlertDialogContent data-testid="activation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la richiesta?</AlertDialogTitle>
            <AlertDialogDescription>{del ? `${del.company_name || del.client_name} · ${del.course_title}` : ""}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="activation-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="activation-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
